import {
	Bell,
	BookOpen,
	ChevronRight,
	Database,
	FileText,
	Folder,
	Headset,
	LayoutDashboard,
	MoreHorizontal,
	NotebookTabs,
	Settings,
	Shield,
	Users,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser, userHasPermission } from '#app/utils/user.ts'
import { isTabActive } from '../mobile-tab-bar.tsx'

// ---------------------------------------------------------------------------
// Tabs config
// ---------------------------------------------------------------------------

type AdminTab =
	| { path: string; label: string; icon: React.ElementType; sheet?: never }
	| {
			path: null
			label: string
			icon: React.ElementType
			sheet: true
	  }

const ADMIN_TABS: AdminTab[] = [
	{ path: '/admin', label: "Aujourd'hui", icon: LayoutDashboard },
	{ path: '/admin/lignes', label: 'Lignes', icon: FileText },
	{ path: '/admin/cartes', label: 'Cartes', icon: NotebookTabs },
	{ path: '/admin/engagements', label: 'Engagements', icon: BookOpen },
	{ path: null, label: 'Plus', icon: MoreHorizontal, sheet: true },
]

// Paths that belong to the "Plus" sheet — Plus tab shows active when on any of these.
const PLUS_PATHS = [
	'/admin/evenements',
	'/admin/support',
	'/admin/alertes',
	'/admin/centre-appel',
	'/admin/categories',
	'/admin/users',
	'/admin/roles',
	'/admin/parametres',
	'/admin/cache',
]

// ---------------------------------------------------------------------------
// Plus-sheet rows config
// ---------------------------------------------------------------------------

const MORE_ROWS = [
	{
		path: '/admin/evenements',
		label: 'Événements',
		icon: LayoutDashboard,
	},
	{
		path: '/admin/support',
		label: 'Soutien',
		icon: FileText,
	},
	{
		path: '/admin/alertes',
		label: 'Alertes',
		icon: Bell,
	},
	{
		path: '/admin/centre-appel',
		label: "Centre d'appel",
		icon: Headset,
	},
	{
		path: '/admin/categories',
		label: 'Catégories',
		icon: Folder,
	},
	{
		path: '/admin/users',
		label: 'Membres',
		icon: Users,
	},
	{
		path: '/admin/roles',
		label: 'Rôles',
		icon: Shield,
	},
	{
		path: '/admin/parametres',
		label: 'Paramètres',
		icon: Settings,
	},
	{
		path: '/admin/cache',
		label: 'Cache',
		icon: Database,
	},
] as const

// ---------------------------------------------------------------------------
// Active-tab helpers
// ---------------------------------------------------------------------------

function isAdminTabActive(pathname: string, tabPath: string): boolean {
	// Exact match only for the today/index page (same as "/" special-case)
	if (tabPath === '/admin') return pathname === '/admin'
	return isTabActive(pathname, tabPath)
}

function isPlusTabActive(pathname: string): boolean {
	return PLUS_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

// ---------------------------------------------------------------------------
// AdminMoreSheet
// ---------------------------------------------------------------------------

function AdminMoreSheet({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const navigate = useNavigate()

	const handleNavigate = (path: string) => {
		onOpenChange(false)
		void navigate(path)
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="bottom" className="gap-0 rounded-t-none border-0 p-0">
				<SheetHeader className="border-border border-b p-4 pr-12">
					<p className="text-plq-rouge flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase">
						<span
							className="bg-plq-rouge h-1.5 w-1.5 rounded-full"
							aria-hidden="true"
						/>
						Administration
					</p>
					<SheetTitle className="text-xl font-bold">
						Plus de sections
					</SheetTitle>
				</SheetHeader>
				<ul className="divide-border/60 divide-y overflow-y-auto">
					{MORE_ROWS.map((row) => {
						const Icon = row.icon
						return (
							<li key={row.path}>
								<button
									type="button"
									onClick={() => handleNavigate(row.path)}
									className="hover:bg-muted flex w-full items-center gap-3 px-4 py-3.5"
								>
									<span className="text-muted-foreground shrink-0">
										<Icon className="h-5 w-5" aria-hidden="true" />
									</span>
									<span className="flex-1 text-left text-sm font-medium">
										{row.label}
									</span>
									<ChevronRight
										className="text-muted-foreground h-4 w-4 shrink-0"
										aria-hidden="true"
									/>
								</button>
							</li>
						)
					})}
				</ul>
			</SheetContent>
		</Sheet>
	)
}

// ---------------------------------------------------------------------------
// AdminTabBar
// ---------------------------------------------------------------------------

export function AdminTabBar() {
	const user = useOptionalUser()
	const { pathname } = useLocation()
	const [moreSheetOpen, setMoreSheetOpen] = useState(false)

	// Same guard as admin _layout.tsx: user must have read:content:any or read:user:any
	const canSeeAdmin =
		userHasPermission(user, 'read:content:any') ||
		userHasPermission(user, 'read:user:any')

	if (!user || !canSeeAdmin) return null

	const plusActive = isPlusTabActive(pathname)

	return (
		<>
			<nav
				aria-label="Navigation administration"
				className="border-border bg-background/75 fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t pb-[env(safe-area-inset-bottom)] backdrop-blur-xl backdrop-saturate-150 xl:hidden"
			>
				{ADMIN_TABS.map((tab) => {
					const Icon = tab.icon

					if (tab.sheet) {
						// Plus tab — opens sheet
						const active = plusActive || moreSheetOpen
						return (
							<button
								key="plus"
								type="button"
								aria-label="Plus de sections"
								aria-expanded={moreSheetOpen}
								onClick={() => setMoreSheetOpen(true)}
								className={cn(
									'flex h-14 flex-col items-center justify-center gap-0.5 px-1 text-[10.5px] leading-tight font-semibold transition-all active:scale-[0.92]',
									active
										? 'text-plq-rouge'
										: 'text-muted-foreground hover:text-foreground',
								)}
							>
								<Icon
									className={cn(
										'h-[22px] w-[22px] shrink-0 transition-transform',
										active && 'scale-110',
									)}
									aria-hidden="true"
								/>
								<span className="text-center">{tab.label}</span>
							</button>
						)
					}

					// Regular tab — link
					const active = isAdminTabActive(pathname, tab.path)
					return (
						<Link
							key={tab.path}
							to={tab.path}
							aria-current={active ? 'page' : undefined}
							className={cn(
								'flex h-14 flex-col items-center justify-center gap-0.5 px-1 text-[10.5px] leading-tight font-semibold transition-all active:scale-[0.92]',
								active
									? 'text-plq-rouge'
									: 'text-muted-foreground hover:text-foreground',
							)}
						>
							<Icon
								className={cn(
									'h-[22px] w-[22px] shrink-0 transition-transform',
									active && 'scale-110',
								)}
								aria-hidden="true"
							/>
							<span className="text-center">{tab.label}</span>
						</Link>
					)
				})}
			</nav>

			<AdminMoreSheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen} />
		</>
	)
}

// ---------------------------------------------------------------------------
// AdminSidebar — desktop (xl+) nav, complements the mobile/tablet tab bar
// ---------------------------------------------------------------------------

const SIDEBAR_GROUPS = [
	{
		label: null,
		items: [{ path: '/admin', label: "Aujourd'hui", icon: LayoutDashboard }],
	},
	{
		label: 'Contenu',
		items: [
			{ path: '/admin/lignes', label: 'Lignes', icon: FileText },
			{ path: '/admin/cartes', label: 'Cartes', icon: NotebookTabs },
			{ path: '/admin/engagements', label: 'Engagements', icon: BookOpen },
			{ path: '/admin/evenements', label: 'Événements', icon: LayoutDashboard },
			{ path: '/admin/support', label: 'Soutien', icon: FileText },
			{ path: '/admin/alertes', label: 'Alertes', icon: Bell },
			{ path: '/admin/categories', label: 'Catégories', icon: Folder },
		],
	},
	{
		label: 'Opérations',
		items: [
			{ path: '/admin/centre-appel', label: "Centre d'appel", icon: Headset },
		],
	},
	{
		label: 'Système',
		items: [
			{ path: '/admin/users', label: 'Membres', icon: Users },
			{ path: '/admin/roles', label: 'Rôles', icon: Shield },
			{ path: '/admin/parametres', label: 'Paramètres', icon: Settings },
			{ path: '/admin/cache', label: 'Cache', icon: Database },
		],
	},
] as const

export function AdminSidebar() {
	const user = useOptionalUser()
	const { pathname } = useLocation()

	const canSeeAdmin =
		userHasPermission(user, 'read:content:any') ||
		userHasPermission(user, 'read:user:any')

	if (!user || !canSeeAdmin) return null

	return (
		<aside
			aria-label="Navigation administration"
			className="border-border bg-background sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r xl:flex"
		>
			<div className="border-border border-b p-6">
				<p className="text-plq-rouge flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase">
					<span
						className="bg-plq-rouge h-1.5 w-1.5 rounded-full"
						aria-hidden="true"
					/>
					Administration
				</p>
			</div>
			<nav className="flex-1 overflow-y-auto p-3">
				{SIDEBAR_GROUPS.map((group, groupIdx) => (
					<div key={groupIdx} className={groupIdx > 0 ? 'mt-6' : undefined}>
						{group.label && (
							<p className="text-muted-foreground mb-2 px-3 text-[10.5px] font-semibold tracking-[0.12em] uppercase">
								{group.label}
							</p>
						)}
						<ul className="flex flex-col gap-0.5">
							{group.items.map((item) => {
								const Icon = item.icon
								const active = isAdminTabActive(pathname, item.path)
								return (
									<li key={item.path}>
										<Link
											to={item.path}
											aria-current={active ? 'page' : undefined}
											className={cn(
												'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
												active
													? 'bg-plq-rouge/10 text-plq-rouge'
													: 'text-foreground hover:bg-muted',
											)}
										>
											<Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
											{item.label}
										</Link>
									</li>
								)
							})}
						</ul>
					</div>
				))}
			</nav>
		</aside>
	)
}
